<script setup lang="ts">
  import { computed, useId } from 'vue';
  import { Button, ButtonColor, ButtonVariant } from '../Button';
  import { Tag, TagColor, TagVariant } from '../Tag';
  import {
    listItemActionClass,
    listItemBodyClass,
    listItemClass,
    listItemDescriptionClass,
    listItemRichClass,
    listItemTitleClass,
  } from './ListItem.css';
  import type { ListItemProps } from './ListItem.types';

  const props = withDefaults(defineProps<ListItemProps>(), {
    iconColor: TagColor.GREY,
  });

  const emit = defineEmits<{
    action: [];
  }>();

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-list-item-${uid}`);

  const isRich = computed(
    () =>
      !!props.title ||
      !!props.description ||
      !!props.iconName ||
      !!props.actionLabel,
  );
</script>

<template>
  <li
    :class="[listItemClass, { [listItemRichClass]: isRich }]"
    :data-testid="resolvedTestId"
  >
    <template v-if="isRich">
      <Tag
        v-if="iconName"
        :variant="TagVariant.ICON"
        :icon-name="iconName"
        :color="iconColor"
        :test-id="`${resolvedTestId}-leading`"
      />
      <div :class="listItemBodyClass">
        <span
          v-if="title"
          :class="listItemTitleClass"
          :data-testid="`${resolvedTestId}-title`"
          >{{
            title
          }}</span
        >
        <span
          v-if="description"
          :class="listItemDescriptionClass"
          :data-testid="`${resolvedTestId}-description`"
          >{{
            description
          }}</span
        >
        <Button
          v-if="actionLabel"
          :variant="ButtonVariant.Inline"
          :color="ButtonColor.Default"
          :icon-name="actionIconName"
          :disabled="actionDisabled"
          :class="listItemActionClass"
          :test-id="`${resolvedTestId}-action`"
          @click="emit('action')"
        >
          {{ actionLabel }}
        </Button>
      </div>
    </template>
    <slot v-else>{{ text }}</slot>
  </li>
</template>
